import React, { PureComponent } from 'react';

import Transition from 'react-transition-group/Transition';

import styled, { css } from 'styled-components';

import Box from '../atoms/Box/Box';
import Circle from '../atoms/Circle/Circle';
import ExternalLink from '../atoms/ExternalLink/ExternalLink';
import Icon from '../atoms/Icon';
import Image from '../atoms/Image/Image';
import Link from '../atoms/Link/Link';
import Text from '../atoms/Text/Text';

import { ROUTE } from '@utils/index';
import analyticsManager from '@utils/analyticsManager';

import { DEFAULT_AVATAR } from '@shared/markup/constants';
import { HaveAccountPopup } from '@hoc/HaveAccountPopup';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { Menu } from '@shared/interfaces/menu-item';
import { ShowHaveAccountPopup } from '@shared/interfaces/show-have-account-popup';
import { Target } from '@shared/enums/target';
import { withTranslation } from 'i18n';

const DefaultImage = styled(Image)`
  height: 1.5em;
  margin-bottom: -0.25em;
`;

const BoxMenu = styled(Box)`
  overflow-y: auto;
  position: fixed;
`;

const MenuItem = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const sharedLinkCss = css`
  display: block;
`;

const LinkMenuItem = styled(Link)`
  ${sharedLinkCss};
`;

const ExternalLinkMenuItem = styled(ExternalLink)`
  ${sharedLinkCss};
`;

const BoxMenuInner = styled.div`
  transition: opacity 300ms ease;
  transition-delay: 300ms;
  opacity: 0;
`;

const AvatarMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
`;

const TextExtend = styled(Text)<{ isActive?: boolean }>`
  ${props =>
    props.isActive &&
    css`
      text-decoration: underline;
      text-underline-offset: 4px;
      text-decoration-color: ${props => props.theme.colors.white};
    `};

  &:hover {
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-color: ${props => props.theme.colors.turquoise};
  }
`;

const HoverableIcon = styled(Icon)`
  position: relative;

  &:hover::after {
    content: '';
    position: absolute;
    bottom: -3px;
    width: 100%;
    height: 2px;
    background-color: ${props => props.theme.colors.turquoise};
    transition: color 0.2s;
  }

  ${props=>
    props.isActive &&
    css`
      &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        width: 100%;
        height: 2px;
        background-color: ${props => props.theme.colors.white};
      }
    `
  }
`

type MobileNavbarProps = {
  className?: string;
  items: Menu[];
  isOpen?: boolean;
  isAuthorized?: boolean;
  avatar?: string;
  userName?: string;
  isActive: boolean;
  hoverColor?: string;
  t: I18nTranslation;
  showHaveAccountPopup?: ShowHaveAccountPopup;
  onItemClick?: () => void;
  pathname?: string;
};

class MobileNavbar extends PureComponent<MobileNavbarProps> {
  ref: React.RefObject<HTMLDivElement> = React.createRef();

  t = key => this.props.t(`header.${key}`);

  handleEntered = elem => {
    this.ref.current.style.opacity = '1';
    elem.style.height = '100%';
  };

  handleExit = (elem?: HTMLDivElement): void => {
    elem.style.height = '0';
  };

  handleAccountClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    this.props.showHaveAccountPopup(null, event);
  };

  renderAvatar = () => {
    const avatar = this.props.avatar ? (
      <Image src={this.props.avatar} height={2.125} />
    ) : (
      <DefaultImage src={DEFAULT_AVATAR} />
    );

    return (
      <LinkMenuItem href={ROUTE.ACCOUNT.INDEX}>
        <AvatarMenuItem pt={1.25} pb={1.25}>
          <Circle backgroundColor="white" size={1.75}>
            {avatar}
          </Circle>
          <Box ml={0.75}>
            <TextExtend
              color='white'
              weight={600}
              lineHeight={1.1}
              size={1.375}
              isActive={this.props.isActive}
            >
              {this.props.userName || this.t('myAccount')}
            </TextExtend>
          </Box>
        </AvatarMenuItem>
      </LinkMenuItem>
    );
  };

  renderAccountLink = () => {

    return (
      <LinkMenuItem
        href={ROUTE.ACCOUNT.INDEX}
        onClick={this.handleAccountClick}
      >
        <MenuItem pt={1.25} pb={1.25}>
          <HoverableIcon name="account" color='white' size={1.25} isActive={this.props.isActive} />
        </MenuItem>
      </LinkMenuItem>
    );
  };

  render() {
    const { isOpen, items, className, isAuthorized } = this.props;

    return (
      <Transition
        className={className}
        in={isOpen}
        timeout={{ enter: 0, exit: 300 }}
        onEntered={this.handleEntered}
        onExit={this.handleExit}
        unmountOnExit
        mountOnEnter
        appear
      >
        <BoxMenu backgroundColor="cornflowerBlue">
          <Box pl={1.25} pr={1.25} pb={5}>
            <BoxMenuInner ref={this.ref}>
              {items.map(item => {
                const Component =
                  item.url[0] === '/' ? LinkMenuItem : ExternalLinkMenuItem;
                const target = item.externalLink ? Target.Blank : Target.Self;

                return (
                  <Component
                    key={item.id}
                    href={item.url}
                    target={target}
                    onClick={(event) => {
                      analyticsManager.google.addItemView(item.id);
                      this.props.onItemClick?.();
                      if (item.url === this.props.pathname) {
                        event.preventDefault();
                        window.location.reload();
                      }
                    }}
                  >
                    <MenuItem pt={1.25} pb={1.25}>
                      <TextExtend
                        isActive={item.isActive}
                        weight={600}
                        lineHeight={1.1}
                        size={1.375}
                        color={item.color}
                      >
                        {this.t(item.id)}
                      </TextExtend>
                    </MenuItem>
                  </Component>
                );
              })}
              {isAuthorized ? this.renderAvatar() : this.renderAccountLink()}
            </BoxMenuInner>
          </Box>
        </BoxMenu>
      </Transition>
    );
  }
}

const styledMobileNavbar = styled(withTranslation('common')(MobileNavbar))`
  position: fixed;
  top: 5em;
  left: 0;
  right: 0;
  z-index: 11;
  transition: height 300ms ease;
  height: 0;
`;

export default HaveAccountPopup(styledMobileNavbar);
